import type { NetworkingTag } from './types'

/**
 * Referência única e estável para o caso "sem vocabulário". `useNetworkingVocabulary`
 * devolve isso direto para um selector de zustand (`useSyncExternalStore` por baixo),
 * que exige que `getSnapshot` devolva a MESMA referência enquanto a store não muda.
 * Um `[] as const` novo a cada chamada quebra esse contrato: o React re-renderiza
 * em loop até estourar "Maximum update depth exceeded" — foi exatamente o que
 * aconteceu ao ligar `useNetworkingVocabulary` num hook global (`useNavigation`).
 */
const EMPTY_VOCABULARY: NetworkingTag[] = []

/**
 * Extrai o vocabulário ativo do payload de dados da gamificação.
 *
 * Tolera `undefined`, e não apenas `[]`. O store usa `persist` com localStorage,
 * então um usuário que volta reidrata um payload gravado antes desta feature, sem
 * a chave `networking_tags` — e `getActiveProductData()` devolve esse objeto em
 * cache, não `EMPTY_GAMIFICATION_DATA`.
 */
export const selectVocabulary = (
  data: { networking_tags?: NetworkingTag[] } | null | undefined
): NetworkingTag[] => data?.networking_tags ?? EMPTY_VOCABULARY

/**
 * Alterna uma tag no conjunto selecionado, preservando a ordem de seleção.
 *
 * Isolado da UI para ser testável sem DOM: o vitest do projeto roda em
 * `environment: 'node'` e o `include` só coleta `.test.ts` / `.test.js`.
 */
export const toggleTagId = (selected: number[], id: number): number[] =>
  selected.includes(id)
    ? selected.filter(selectedId => selectedId !== id)
    : [...selected, id]

/**
 * Vocabulário oferecido ao participante: as tags ativas do evento, mais as que
 * ele já declarou e o produtor desativou depois.
 *
 * Sem a segunda parte, um chip selecionado apareceria como id sem nome — o
 * vocabulário ativo do payload não contém tags desativadas (RF-04), enquanto a
 * leitura da declaração contém (RF-17).
 */
export const mergeDeclaredIntoVocabulary = (
  activeVocabulary: NetworkingTag[],
  declaredTags: NetworkingTag[]
): NetworkingTag[] => {
  const activeIds = new Set(activeVocabulary.map(tag => tag.id))
  const declaredOnly = declaredTags.filter(tag => !activeIds.has(tag.id))

  return [...activeVocabulary, ...declaredOnly]
}

/**
 * Uma tag está fora do vocabulário ativo quando o participante a declarou e o
 * produtor a desativou depois. A UI a mantém selecionável para remoção, mas não
 * a oferece como escolha nova.
 */
export const isRetiredTag = (
  tag: NetworkingTag,
  activeVocabulary: NetworkingTag[]
): boolean => !activeVocabulary.some(activeTag => activeTag.id === tag.id)
